/**
 * CompanyCertificatesSection — Upload de certificados digitais (e-CNPJ e e-CPF)
 * vinculados a uma empresa específica.
 *
 * Reaproveita a infraestrutura da Fase 4.2 eSocial:
 *   • Endpoint backend: POST /api/companies/{company_id}/certificados/upload
 *   • Senha cifrada com Fernet (AES-128)
 *   • Extração automática de titular, CNPJ/CPF, validade via SAN ICP-Brasil
 *
 * Para transmissão eSocial em produção é OBRIGATÓRIO o e-CPF do
 * representante legal — o e-CNPJ sozinho não é aceito pelo gov.br.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatCPF, formatCNPJ } from '../../lib/helpers';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  ShieldCheck, Upload, Trash2, AlertTriangle, Calendar,
  Building2, User as UserIcon, Loader2, CheckCircle2, FileSignature,
} from 'lucide-react';
import { useConfirm } from '../../hooks/useConfirm';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? '—' : dt.toLocaleDateString('pt-BR');
};



const CertCard = ({ cert, onDelete, tipo }) => {
  const expirando =
    cert.dias_para_expirar !== null &&
    cert.dias_para_expirar >= 0 &&
    cert.dias_para_expirar <= 30;
  return (
    <div
      className={`border rounded-md p-3 flex items-start gap-3 ${
        cert.ativo ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'
      }`}
      data-testid={`company-cert-${tipo}-card`}
    >
      <div className={`p-2 rounded ${cert.ativo ? 'bg-emerald-100' : 'bg-slate-100'}`}>
        <ShieldCheck
          className={`h-5 w-5 ${cert.ativo ? 'text-emerald-600' : 'text-slate-500'}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800 truncate">
            {cert.titular || '(sem titular)'}
          </span>
          {cert.ativo && (
            <Badge className="bg-emerald-600 text-white text-[10px]">ATIVO</Badge>
          )}
          {cert.expirado && (
            <Badge variant="destructive" className="text-[10px]">EXPIRADO</Badge>
          )}
          {!cert.expirado && expirando && (
            <Badge className="bg-amber-500 text-white text-[10px]">
              EXPIRA EM {cert.dias_para_expirar}d
            </Badge>
          )}
        </div>
        <div className="text-xs text-slate-600 mt-1 grid grid-cols-2 gap-x-4">
          <div>
            <Building2 className="h-3 w-3 inline mr-1" />
            {tipo === 'eCNPJ' ? 'CNPJ' : 'CPF'}:{' '}
            <code className="font-mono">{tipo === 'eCNPJ' ? formatCNPJ(cert.titular_cpf_cnpj) : formatCPF(cert.titular_cpf_cnpj)}</code>
          </div>
          <div>Emissor: {cert.emissor || '—'}</div>
          <div>
            <Calendar className="h-3 w-3 inline mr-1" />
            Válido até: {fmtDate(cert.valido_ate)}
          </div>
          {tipo === 'eCPF' && cert.representante_nome && (
            <div className="col-span-2">
              <UserIcon className="h-3 w-3 inline mr-1" />
              Representante: {cert.representante_nome}
              {cert.representante_cpf && ` (${formatCPF(cert.representante_cpf)})`}
            </div>
          )}
          {cert.descricao && (
            <div className="col-span-2 italic text-slate-500">"{cert.descricao}"</div>
          )}
        </div>
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onDelete(cert.id)}
        title="Excluir"
        data-testid={`company-cert-${tipo}-delete-btn`}
      >
        <Trash2 className="h-4 w-4 text-red-600" />
      </Button>
    </div>
  );
};

const UploadDialog = ({ open, onOpenChange, tipo, companyId, onUploaded }) => {
  const [arquivo, setArquivo] = useState(null);
  const [senha, setSenha] = useState('');
  const [descricao, setDescricao] = useState('');
  const [repNome, setRepNome] = useState('');
  const [repCpf, setRepCpf] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setArquivo(null);
      setSenha('');
      setDescricao('');
      setRepNome('');
      setRepCpf('');
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [open]);

  const submit = async () => {
    if (!arquivo) return toast.error('Selecione o arquivo .pfx ou .p12');
    if (!senha) return toast.error('Informe a senha do certificado');
    if (tipo === 'eCPF' && !repNome.trim()) {
      return toast.error('Informe o nome do representante legal');
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('senha', senha);
      fd.append('tipo', tipo);
      if (descricao) fd.append('descricao', descricao);
      if (tipo === 'eCPF') {
        fd.append('representante_nome', repNome.trim());
        if (repCpf) fd.append('representante_cpf', repCpf.replace(/\D/g, ''));
      }
      await axios.post(
        `${API}/api/companies/${companyId}/certificados/upload`,
        fd,
        { ...auth(), headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(`✅ Certificado ${tipo} carregado com sucesso`);
      onUploaded();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao processar certificado');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Upload de Certificado {tipo === 'eCNPJ' ? 'e-CNPJ (Empresa)' : 'e-CPF (Representante Legal)'}
          </DialogTitle>
          <DialogDescription>
            {tipo === 'eCNPJ'
              ? 'Certificado da pessoa jurídica. Usado em alguns serviços fiscais.'
              : 'OBRIGATÓRIO para transmissão eSocial. Identifica o representante legal da empresa perante o gov.br.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>Arquivo (.pfx ou .p12) *</Label>
            <Input
              ref={fileRef}
              type="file"
              accept=".pfx,.p12,application/x-pkcs12"
              onChange={(e) => setArquivo(e.target.files?.[0] || null)}
              data-testid={`company-cert-${tipo}-file-input`}
            />
          </div>
          <div>
            <Label>Senha do certificado *</Label>
            <Input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="••••••••"
              data-testid={`company-cert-${tipo}-senha-input`}
            />
          </div>
          {tipo === 'eCPF' && (
            <>
              <div>
                <Label>Nome do representante legal *</Label>
                <Input
                  value={repNome}
                  onChange={(e) => setRepNome(e.target.value)}
                  placeholder="Ex: JOÃO DA SILVA"
                  data-testid="company-cert-eCPF-rep-nome-input"
                />
              </div>
              <div>
                <Label>CPF do representante (opcional)</Label>
                <Input
                  value={repCpf}
                  onChange={(e) => setRepCpf(e.target.value)}
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>
            </>
          )}
          <div>
            <Label>Descrição (opcional)</Label>
            <Input
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder={`Ex: ${tipo} 2026 — ${tipo === 'eCNPJ' ? 'Matriz' : 'Diretor Financeiro'}`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={uploading}
            data-testid={`company-cert-${tipo}-submit-btn`}
          >
            {uploading ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processando…</>
            ) : (
              <><CheckCircle2 className="h-4 w-4 mr-2" />Carregar Certificado</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CompanyCertificatesSection = ({ companyId }) => {
  const { confirm, ConfirmDialog } = useConfirm();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTipo, setUploadTipo] = useState('eCNPJ');

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/api/companies/${companyId}/certificados`,
        auth()
      );
      setCerts(r.data || []);
    } catch (e) {
      toast.error('Erro ao listar certificados da empresa');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (certId) => {
    const ok = await confirm({
      title: 'Excluir certificado?',
      description:
        'Esta ação é permanente. Se for o certificado em uso, você precisará cadastrar outro antes de assinar/transmitir eventos eSocial.',
      confirmLabel: 'Excluir',
      destructive: true,
    });
    if (!ok) return;
    try {
      await axios.delete(
        `${API}/api/companies/${companyId}/certificados/${certId}`,
        auth()
      );
      toast.success('Certificado excluído');
      load();
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const openUpload = (tipo) => {
    setUploadTipo(tipo);
    setUploadOpen(true);
  };

  const ecnpj = certs.filter((c) => c.tipo === 'eCNPJ');
  const ecpf = certs.filter((c) => c.tipo === 'eCPF');

  if (!companyId) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4 text-sm text-slate-600 flex gap-3">
          <AlertTriangle className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
          <span>
            Salve a empresa primeiro para poder fazer upload dos certificados digitais.
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="company-certificates-section">
      <ConfirmDialog />

      {/* e-CNPJ */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-indigo-600" />
            <h4 className="font-semibold text-sm text-slate-800">
              Certificado e-CNPJ (Empresa)
            </h4>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openUpload('eCNPJ')}
            data-testid="company-cert-eCNPJ-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1" /> Carregar e-CNPJ
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : ecnpj.length === 0 ? (
          <div className="border border-dashed rounded-md p-4 text-center text-xs text-slate-500">
            <FileSignature className="h-6 w-6 mx-auto mb-1 text-slate-300" />
            Nenhum e-CNPJ cadastrado para esta empresa.
          </div>
        ) : (
          <div className="space-y-2">
            {ecnpj.map((c) => (
              <CertCard key={c.id} cert={c} tipo="eCNPJ" onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>

      {/* e-CPF (representante legal) */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserIcon className="h-4 w-4 text-purple-600" />
            <h4 className="font-semibold text-sm text-slate-800">
              Certificado e-CPF (Representante Legal)
            </h4>
            <Badge className="bg-amber-100 text-amber-800 text-[10px]">
              Obrigatório p/ eSocial
            </Badge>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => openUpload('eCPF')}
            data-testid="company-cert-eCPF-upload-btn"
          >
            <Upload className="h-4 w-4 mr-1" /> Carregar e-CPF
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : ecpf.length === 0 ? (
          <div className="border border-dashed rounded-md p-4 text-center text-xs text-slate-500">
            <FileSignature className="h-6 w-6 mx-auto mb-1 text-slate-300" />
            Nenhum e-CPF cadastrado. Necessário para transmissão eSocial ao gov.br.
          </div>
        ) : (
          <div className="space-y-2">
            {ecpf.map((c) => (
              <CertCard key={c.id} cert={c} tipo="eCPF" onDelete={onDelete} />
            ))}
          </div>
        )}
      </div>

      <Card className="bg-amber-50/50 border-amber-200">
        <CardContent className="p-3 flex gap-3 text-xs">
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            A senha do certificado é armazenada <b>criptografada</b> (Fernet/AES-128).
            Para transmissão real ao gov.br defina{' '}
            <code className="bg-amber-100 px-1">ESOCIAL_TRANSMISSAO_MODO=real</code>{' '}
            e use um e-CPF ICP-Brasil válido emitido pela sua AC.
          </div>
        </CardContent>
      </Card>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        tipo={uploadTipo}
        companyId={companyId}
        onUploaded={load}
      />
    </div>
  );
};

export default CompanyCertificatesSection;
